import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import ProductCheckoutModal from './ProductCheckoutModal';
import Icon from '../../components/AppIcon';
import { selectCustomerId } from '../../features/auth/authSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';
import {
  useGetMonetizationConfigQuery,
  useGetMonetizationMineQuery,
  useGetMyAgentPropertiesQuery,
} from '../../features/api/apiSlice';

const PLACEMENTS = [
  { value: 'home_banner', label: 'Home banner' },
  { value: 'search_sidebar', label: 'Search sidebar' },
  { value: 'app_feed', label: 'App feed' },
  { value: 'notice_board', label: 'Notice board' },
];

const PRODUCT_ICONS = {
  lead_fee: 'Contact',
  featured_agent: 'Star',
  video_tour: 'Video',
  platform_ad: 'Megaphone',
  boost: 'Zap',
};

const AgentPromote = () => {
  const customerId = useSelector(selectCustomerId);
  const { formatPrice } = useCurrency();
  const [params, setParams] = useSearchParams();
  const { data: monetization } = useGetMonetizationConfigQuery();
  const { data: mine, refetch } = useGetMonetizationMineQuery();
  const { data: propsData } = useGetMyAgentPropertiesQuery(
    { userid: customerId, limit: 100 },
    { skip: !customerId }
  );

  const [banner, setBanner] = useState(null);
  const [checkout, setCheckout] = useState(null);

  const properties = propsData?.data || [];
  const products = monetization?.products || mine?.products || [];
  const leadFees = mine?.lead_fees || [];
  const pendingLeads = leadFees.filter((c) => c.status === 'pending');

  useEffect(() => {
    const status = params.get('status');
    if (!status) return;
    if (status === 'success') {
      setBanner({ tone: 'ok', text: 'Payment received. Your purchase is active.' });
      refetch();
    } else if (status === 'cancelled') {
      setBanner({ tone: 'warn', text: 'Payment was cancelled. You can try again.' });
    } else if (status === 'failed') {
      setBanner({ tone: 'err', text: 'Payment did not go through. Try another method.' });
    }
    const next = new URLSearchParams(params);
    next.delete('status');
    setParams(next, { replace: true });
  }, [params, refetch, setParams]);

  const catalog = useMemo(
    () =>
      (products || []).filter((p) =>
        ['lead_fee', 'featured_agent', 'video_tour', 'platform_ad'].includes(p.id)
      ),
    [products]
  );

  const openProduct = (p, extra = {}) => {
    const extraFields = [];
    const extraPayload = { ...extra };

    if (p.id === 'video_tour') {
      extraFields.push({
        name: 'property_id',
        label: 'Listing',
        type: 'select',
        required: true,
        options: [
          { value: '', label: 'Select a listing' },
          ...properties.map((row) => ({ value: String(row.id), label: row.title || `#${row.id}` })),
        ],
      });
      extraFields.push({
        name: 'video_url',
        label: 'Video URL (YouTube, Vimeo, or MP4)',
        type: 'url',
        placeholder: 'https://',
      });
      extraFields.push({
        name: 'three_d_url',
        label: '3D tour URL (optional)',
        type: 'url',
        placeholder: 'https://',
      });
    }

    if (p.id === 'platform_ad') {
      extraFields.push({
        name: 'placement',
        label: 'Placement',
        type: 'select',
        required: true,
        defaultValue: 'home_banner',
        options: PLACEMENTS,
      });
      extraFields.push({
        name: 'title',
        label: 'Advertisement title',
        required: true,
        placeholder: 'Showroom opening in Kampala',
      });
      extraFields.push({
        name: 'advertiser_name',
        label: 'Advertiser name',
        placeholder: 'Your company',
      });
      extraFields.push({
        name: 'image_url',
        label: 'Image URL',
        type: 'url',
        placeholder: 'https://',
      });
      extraFields.push({
        name: 'link_url',
        label: 'Click-through URL',
        type: 'url',
        placeholder: 'https://',
      });
    }

    setCheckout({
      product: p.id,
      title: p.label,
      description: p.description,
      fee: extra.fee != null ? extra.fee : p.fee_ugx,
      days: p.days || 0,
      extraPayload,
      extraFields,
    });
  };

  return (
    <AgentLayout
      title="Promote"
      subtitle="Pay for leads, featured placement, video tours, and advertisement space with Flutterwave, MTN, or Airtel Money."
    >
      {banner && (
        <div
          className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
            banner.tone === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : banner.tone === 'warn'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
        >
          {banner.text}
        </div>
      )}

      {mine?.featured && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          You are featured in the agent directory
          {mine.featured_until ? ` until ${new Date(mine.featured_until).toLocaleDateString()}` : ''}.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {catalog.map((p) => (
          <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-800 flex items-center justify-center">
                <Icon name={PRODUCT_ICONS[p.id] || 'Wallet'} size={18} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{p.label}</h3>
                <p className="text-sm text-slate-500 mt-0.5">{p.description}</p>
              </div>
            </div>
            <p className="text-lg font-bold text-slate-900 mb-3">
              {p.enabled ? (p.fee_ugx > 0 ? formatPrice(p.fee_ugx, 'UGX') : 'No fee') : 'Off'}
              {p.enabled && p.days > 0 ? (
                <span className="text-xs font-medium text-slate-500"> · {p.days} days</span>
              ) : null}
            </p>
            {p.enabled ? (
              p.id === 'lead_fee' ? (
                <p className="text-xs text-slate-500">
                  Fees appear below when a client books you. Pay each charge here.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => openProduct(p)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800"
                >
                  Pay with Flutterwave / MoMo
                </button>
              )
            ) : (
              <p className="text-xs text-slate-400">Admin has this product off. It stays hidden from shoppers until enabled.</p>
            )}
          </div>
        ))}
      </div>

      {pendingLeads.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white mb-8 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Lead generation fees due</h3>
            <p className="text-xs text-slate-500">
              Pending {formatPrice(mine?.lead_pending_total || 0, 'UGX')}
            </p>
          </div>
          <ul className="divide-y divide-slate-100">
            {pendingLeads.map((c) => (
              <li key={c.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {c.notes || `Lead fee #${c.id}`}
                  </p>
                  <p className="text-xs text-slate-500">{c.source}</p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    openProduct(
                      {
                        id: 'lead_fee',
                        label: 'Lead generation',
                        description: c.notes || `Settle lead fee #${c.id}`,
                        fee_ugx: c.fee_amount,
                        days: 0,
                      },
                      { charge_id: c.id, fee: c.fee_amount }
                    )
                  }
                  className="text-sm font-semibold text-amber-800 hover:text-amber-900"
                >
                  Pay {formatPrice(c.fee_amount, 'UGX')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(mine?.ads?.length || mine?.video_tours?.length || mine?.boosts?.length) ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Active purchases</h3>
          <ul className="space-y-2 text-sm text-slate-700">
            {(mine.boosts || []).map((b) => (
              <li key={`b-${b.id}`}>Boosted: {b.title} until {new Date(b.boosted_until).toLocaleDateString()}</li>
            ))}
            {(mine.video_tours || []).map((t) => (
              <li key={`t-${t.id}`}>Video tour: {t.title} until {new Date(t.video_tour_until).toLocaleDateString()}</li>
            ))}
            {(mine.ads || []).map((a) => (
              <li key={`a-${a.id}`}>
                Ad: {a.title} ({String(a.placement || '').replace('_', ' ')})
                {a.ends_at ? ` until ${new Date(a.ends_at).toLocaleDateString()}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {checkout && (
        <ProductCheckoutModal
          product={checkout.product}
          title={checkout.title}
          description={checkout.description}
          fee={checkout.fee}
          days={checkout.days}
          extraPayload={checkout.extraPayload}
          extraFields={checkout.extraFields}
          monetization={monetization}
          onClose={() => setCheckout(null)}
          onPaid={() => {
            setCheckout(null);
            setBanner({ tone: 'ok', text: 'Payment received. Your purchase is active.' });
            refetch();
          }}
        />
      )}
    </AgentLayout>
  );
};

export default AgentPromote;
